import { useQuery } from '@tanstack/react-query';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterOutputs } from '../../api/trpc';
import { isRunActive } from '../../lib/runs';

export type PipelineState = RouterOutputs['pipeline']['state'];
export type PipelineStage = PipelineState['stages'][number];
export type PipelineStageKey = PipelineStage['key'];
export type PipelineStageStatus = PipelineStage['status'];
export type PipelineNextAction = PipelineState['nextAction'];
export type PipelineNextActionKind = PipelineNextAction['kind'];
export type PipelineActiveRun = NonNullable<PipelineState['activeRun']>;
/** Machine-readable twin of a prose line (`code` + params) — see `messages.ts`. */
export type PipelineMessage = PipelineStage['detailMsgs'][number];

/** Guided pipeline state; polls fast while something runs, slowly otherwise. */
export function usePipelineState(enabled: boolean) {
  return useQuery(
    trpc.pipeline.state.queryOptions(undefined, {
      enabled,
      refetchInterval: (query) => {
        const data = query.state.data;
        return data && (data.activeRun !== null || data.nextAction.kind === 'wait') ? 5000 : 30_000;
      },
    }),
  );
}

/** Sync runs (refresh + snapshot), polled every 5 s while one is queued/running. */
export function useSyncRuns(enabled = true) {
  return useQuery(
    trpc.pipedrive.listRuns.queryOptions(undefined, {
      enabled,
      refetchInterval: (query) => (query.state.data?.some((r) => isRunActive(r.status)) ? 5000 : false),
    }),
  );
}

/** Dedup runs, polled every 5 s while one is queued/running. */
export function useDedupRuns(enabled = true) {
  return useQuery(
    trpc.dedup.listRuns.queryOptions(undefined, {
      enabled,
      refetchInterval: (query) => (query.state.data?.some((r) => isRunActive(r.status)) ? 5000 : false),
    }),
  );
}

/** The guided pipeline's view changed (a run was started, a decision made). */
export function invalidatePipelineState(): Promise<void> {
  return invalidateAll(trpc.pipeline.pathFilter());
}

/** Drop every cached tRPC query of the modules the pipeline touches. */
export function invalidateTrpcPipeline(): Promise<void> {
  return invalidateAll(trpc.pipedrive.pathFilter(), trpc.dedup.pathFilter(), trpc.pipeline.pathFilter());
}
